import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { FileBarChart, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useApiKeySummary, useGenerateApiReport } from '../api';
import { SUMMARY_COLUMNS } from '../columns';
import type { ApiKeySummaryRow } from '../types';

export function ApiKeySummaryPage() {
  const userId = useAuthStore((s) => s.userId);
  const query = useApiKeySummary();
  const generate = useGenerateApiReport();
  const [pendingId, setPendingId] = useState<number | null>(null);

  const rows = query.data ?? [];

  const handleGenerate = useCallback(
    (row: ApiKeySummaryRow, reportType: 'api' | 'api_webhook') => {
      if (!row.apiKeyId) {
        toast.error('No API key bound to this row.');
        return;
      }
      setPendingId(row.id);
      generate.mutate(
        {
          apiId: row.apiKeyId,
          reportType,
          scheduleDate: row.scheduleDate,
          userId: Number(userId),
        },
        {
          onSuccess: (res) => {
            toast.success(res?.message ?? 'Report queued');
            setPendingId(null);
          },
          onError: () => {
            toast.error('Could not generate the report.');
            setPendingId(null);
          },
        },
      );
    },
    [generate, userId],
  );

  const columns = useMemo<ColumnDef<ApiKeySummaryRow>[]>(
    () => [
      ...SUMMARY_COLUMNS,
      {
        id: 'actions',
        header: 'Settings',
        size: 110,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isPending = pendingId === r.id && generate.isPending;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Row actions"
                  disabled={isPending}
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Reports</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleGenerate(r, 'api')}>
                  <FileBarChart className="h-4 w-4" />
                  Generate full report
                </DropdownMenuItem>
                {r.hasWebhook === '1' && (
                  <DropdownMenuItem onClick={() => handleGenerate(r, 'api_webhook')}>
                    <FileBarChart className="h-4 w-4" />
                    Generate webhook report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [generate.isPending, pendingId, handleGenerate],
  );

  const canViewSummary = usePermission('Manage Api Key.Api Summary', BASE_PERMISSION.VIEW);

  if (!canViewSummary) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Usage"
        description="Current-day usage rollup per API key."
      />

      <DataTable<ApiKeySummaryRow, unknown>
        tableId="api-keys:summary:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search summary…"
        emptyTitle="No summary rows"
        emptyDescription="Run an API job to populate this view."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="api-key-summary.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
